import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  LogOut, ClipboardList, Users, Calendar, Bell, TrendingUp, BookOpen, 
  BarChart3, Package, DollarSign, Brain, User, MessageSquare, CalendarDays 
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import BatchManagement from '../components/BatchManagement';
import TaskManagement from '../components/TaskManagement';
import AttendanceMarking from '../components/AttendanceMarking';
import Reports from '../components/Reports';
import Notifications from '../components/Notifications';
import StudentManagement from '../components/StudentManagement';
import Analytics from '../components/Analytics';
import TeacherKitRequests from '../components/TeacherKitRequests';
import FeesManagement from '../components/FeesManagement';
import MentalGames from '../components/MentalGames';
import LiveExamMode from '../components/LiveExamMode';
import TeacherProfile from '../components/TeacherProfile';
import MessagingSystem from '../components/MessagingSystem';
import ScheduleManagement from '../components/ScheduleManagement';
import AIAnalyticsDashboard from '../components/AIAnalyticsDashboard';
import StudentProgress from '../components/StudentProgress';

const TeacherDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats] = useState({
    myTasks: 0,
    myStudents: 0,
    pendingTasks: 0
  });
  const [analyticsOverview, setAnalyticsOverview] = useState(null);
  const [feeMetrics, setFeeMetrics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user'));

  useEffect(() => {
    loadStats();
    loadNotifications();
    loadAnalyticsOverview();
    loadFeeMetrics();
    loadUnreadMessageCount();
    
    const messageInterval = setInterval(() => {
      loadUnreadMessageCount();
    }, 30000);
    
    return () => clearInterval(messageInterval);
  }, []);

  const loadStats = async () => {
    try {
      const [tasks, students] = await Promise.all([
        axios.get(`${API}/tasks`),
        axios.get(`${API}/users?role=student&center_id=${user.center_id}`)
      ]);
      const pending = tasks.data.filter(t => t.status === 'pending').length;
      setStats({
        myTasks: tasks.data.length,
        myStudents: students.data.length,
        pendingTasks: pending
      });
    } catch (error) {
      console.error('Failed to load stats', error);
    }
  };

  const loadAnalyticsOverview = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalyticsOverview(response.data);
    } catch (error) {
      console.error('Failed to load analytics overview', error);
    }
  };

  const loadFeeMetrics = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/fees/dashboard-metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeeMetrics(response.data);
    } catch (error) {
      console.error('Failed to load fee metrics');
    }
  };

  const loadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to load notifications', error);
    }
  };

  const loadUnreadMessageCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/messages/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadMessageCount(response.data.unread_count);
    } catch (error) {
      console.error('Failed to load unread message count', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
    toast.success('Logged out successfully');
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: TrendingUp },
    { id: 'ai-analytics', label: 'AI Analytics', icon: Brain },
    { id: 'messages', label: 'Messages', icon: MessageSquare, badge: unreadMessageCount > 0 ? unreadMessageCount : null },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
    { id: 'fees', label: 'Fees', icon: DollarSign },
    { id: 'kit', label: 'Kit Requests', icon: Package },
    { id: 'students', label: 'Students', icon: Users },
    { id: 'batches', label: 'Batches', icon: BookOpen },
    { id: 'tasks', label: 'Tasks', icon: ClipboardList },
    { id: 'attendance', label: 'Attendance', icon: Calendar },
    { id: 'games', label: 'Games', icon: ClipboardList },
    { id: 'reports', label: 'Reports', icon: TrendingUp }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex" data-testid="teacher-dashboard">
      <Sidebar
        menuItems={menuItems}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        brandColor="emerald"
        header={(isOpen) => (
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
              <img 
                src="/logo.svg" 
                alt="GeniusKid" 
                className="w-10 h-10 object-contain"
              />
            </div>
            {isOpen && (
              <div>
                <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  GeniusKid
                </h1>
                <p className="text-xs text-gray-400">Teacher Panel</p>
              </div>
            )}
          </div>
        )}
        footer={(isOpen) => (
          <div className="space-y-2">
            <button
              onClick={() => setActiveTab('profile')}
              className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors ${!isOpen && 'justify-center'}`}
            >
              <User className="w-5 h-5" />
              {isOpen && <span className="text-sm">My Profile</span>}
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`w-full flex items-center space-x-3 px-4 py-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors relative ${!isOpen && 'justify-center'}`}
            >
              <Bell className="w-5 h-5" />
              {isOpen && <span className="text-sm">Notifications</span>}
              {unreadCount > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              )}
            </button>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className={`w-full text-gray-300 hover:bg-gray-800 hover:text-white ${!isOpen && 'px-2'}`}
              data-testid="logout-button"
            >
              <LogOut className="w-5 h-5" />
              {isOpen && <span className="ml-3">Logout</span>}
            </Button>
          </div>
        )}
      />

      <div className="flex-1 flex flex-col min-h-screen">
        <div className="bg-white border-b border-gray-200 shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {menuItems.find(m => m.id === activeTab)?.label || 'Dashboard'}
                </h2>
                <p className="text-sm text-gray-600">Welcome, {user?.name}</p>
              </div>
              <div className="flex items-center space-x-3">
                <div className="text-right">
                  <p className="text-xs text-gray-500">{user?.is_center_head ? 'Center Head' : 'Teacher'}</p>
                  <p className="text-sm font-semibold text-gray-900">{user?.email}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {unreadMessageCount > 0 && activeTab !== 'messages' && (
          <div className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white py-3 px-6 shadow-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <MessageSquare className="w-5 h-5" />
                <span className="font-medium">
                  You have {unreadMessageCount} unread message{unreadMessageCount !== 1 ? 's' : ''} from Head Office
                </span>
              </div>
              <Button
                onClick={() => setActiveTab('messages')}
                size="sm"
                variant="secondary"
                className="bg-white text-emerald-600 hover:bg-emerald-50"
              >
                View Messages
              </Button>
            </div>
          </div>
        )}

        <div className="flex-1 p-6 overflow-auto">
          {activeTab === 'overview' && (
            <div className="space-y-6 animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card className="p-6 bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow" data-testid="stat-tasks">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100 text-sm mb-1">My Tasks</p>
                      <p className="text-3xl font-bold">{stats.myTasks}</p>
                    </div>
                    <ClipboardList className="w-12 h-12 text-emerald-200 opacity-80" />
                  </div>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow" data-testid="stat-students">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-teal-100 text-sm mb-1">Total Students</p>
                      <p className="text-3xl font-bold">{stats.myStudents}</p>
                    </div>
                    <Users className="w-12 h-12 text-teal-200 opacity-80" />
                  </div>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-cyan-500 to-cyan-600 text-white border-0 shadow-lg hover:shadow-xl transition-shadow" data-testid="stat-pending">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-cyan-100 text-sm mb-1">Pending Tasks</p>
                      <p className="text-3xl font-bold">{stats.pendingTasks}</p>
                    </div>
                    <ClipboardList className="w-12 h-12 text-cyan-200 opacity-80" />
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('messages')}>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-6 h-6 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">View Messages</h3>
                      <p className="text-sm text-gray-600">From Head Office</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('students')}>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <Users className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Manage Students</h3>
                      <p className="text-sm text-gray-600">View & edit</p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => setActiveTab('games')}>
                  <div className="flex items-center space-x-4">
                    <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                      <Brain className="w-6 h-6 text-cyan-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Mental Games</h3>
                      <p className="text-sm text-gray-600">Start live exam</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          )}

          {activeTab === 'ai-analytics' && <AIAnalyticsDashboard userRole="teacher" />}
          {activeTab === 'messages' && <MessagingSystem userRole="teacher" />}
          {activeTab === 'analytics' && <Analytics />}
          {activeTab === 'fees' && (
            <div className="space-y-6">
              <FeesManagement user={user} />
            </div>
          )}
          {activeTab === 'kit' && <TeacherKitRequests />}
          {activeTab === 'students' && <StudentManagement />}
          {activeTab === 'batches' && <BatchManagement />}
          {activeTab === 'tasks' && <TaskManagement />}
          {activeTab === 'attendance' && <AttendanceMarking />}
          {activeTab === 'reports' && <Reports />}
          {activeTab === 'notifications' && <Notifications notifications={notifications} onUpdate={loadNotifications} />}
          {activeTab === 'profile' && <TeacherProfile user={user} />}
          {activeTab === 'games' && (
            <div className="space-y-6">
              <LiveExamMode user={user} />
              <div className="border-t pt-6">
                <MentalGames user={user} />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeacherDashboard;